import { useMemo } from 'react';
import { PhoneAnalyticsTypes } from '@frontend/api-analytics';
import { AnalyticsUtils, DemoChip, InfoTipPopover } from '@frontend/analytics-shared';
import { BarChartData, Chart } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { useFetchAggregatedCallsAndDurationReport } from '../../hooks/use-fetch-aggregated-calls-and-duration-report';
import { usePhoneAnalyticsShallowStore } from '../../hooks/use-phone-analytics-store';
import { ChartProps } from './shared-types';

const colors = {
  abandoned: theme.colors.warning50,
  answered: theme.colors.secondary.seaweed30,
  missed: theme.colors.critical30,
};

const labels = {
  abandoned: i18next.t('Abandoned', { ns: 'analytics-phone' }),
  answered: i18next.t('Answered', { ns: 'analytics-phone' }),
  missed: i18next.t('Missed', { ns: 'analytics-phone' }),
};

type ProcessedTotals = {
  calls: {
    abandoned: number;
    answered: number;
    missed: number;
  };
  duration: {
    abandoned: number;
    answered: number;
    missed: number;
  };
};

type ProcessedData = {
  chartData: BarChartData;
  average: {
    abandoned: number;
    answered: number;
    missed: number;
  };
};

export const AverageIncomingCallDurationChart = ({
  defaultChartAppearance = {},
  exportPdfProps,
  isSmallMode,
}: ChartProps) => {
  const { t } = useTranslation('analytics-phone');
  const { accessibleLocationsNames, filterHintText, isDemoAccount } = usePhoneAnalyticsShallowStore(
    'accessibleLocationsNames',
    'filterHintText',
    'isDemoAccount'
  );

  const { data, isHourlyData, isLoading, isMultiLocation } =
    useFetchAggregatedCallsAndDurationReport<PhoneAnalyticsTypes.IncomingCallsAggregationKey>('incoming_call_status');

  const processeData: ProcessedData = useMemo(() => {
    let groups: BarChartData['groups'] = [];

    if (isMultiLocation) {
      // There will be multiple location objects with single/multiple dates
      groups = Object.entries(data?.aggregatedMultiLocations || {}).map(([key, { calls, duration }]) => {
        const averageAbandoned = (duration?.abandoned || 0) / (calls?.abandoned || 1);
        const averageAnswered = (duration?.answered || 0) / (calls?.answered || 1);
        const averageMissed = (duration?.missed || 0) / (calls?.missed || 1);

        return {
          name: accessibleLocationsNames?.[key] || key,
          values: {
            abandoned: averageAbandoned,
            answered: averageAnswered,
            missed: averageMissed,
          },
        };
      });
    } else {
      // For hourly data: There will be only one object with single date and hourly data for the given location
      // For daily data: There will be only one object with multiple dates for the given location
      const locationData = Object.values(data?.locations || {})[0] || {};

      if (isHourlyData) {
        // Hourly data is restricted to a single date, hence only one object
        const { abandoned, answered, missed } = Object.values(locationData || {})[0] || {};
        const abandonedHourlyCalls = abandoned?.calls?.hourly || [];
        const abandonedHourlyDuration = abandoned?.duration?.hourly || [];
        const answeredHourlyCalls = answered?.calls?.hourly || [];
        const answeredHourlyDuration = answered?.duration?.hourly || [];
        const missedHourlyCalls = missed?.calls?.hourly || [];
        const missedHourlyDuration = missed?.duration?.hourly || [];

        groups = Array.from({ length: 24 }).map((_, i) => {
          const averageAbandoned = (abandonedHourlyDuration[i] || 0) / (abandonedHourlyCalls[i] || 1);
          const averageAnswered = (answeredHourlyDuration[i] || 0) / (answeredHourlyCalls[i] || 1);
          const averageMissed = (missedHourlyDuration[i] || 0) / (missedHourlyCalls[i] || 1);

          return {
            name: AnalyticsUtils.getHourlyInsightsLabel(i),
            values: {
              abandoned: averageAbandoned,
              answered: averageAnswered,
              missed: averageMissed,
            },
          };
        });
      } else {
        groups = Object.entries(locationData).map(([date, { abandoned, answered, missed }]) => {
          const averageAbandoned = (abandoned?.duration?.daily || 0) / (abandoned?.calls?.daily || 1);
          const averageAnswered = (answered?.duration?.daily || 0) / (answered?.calls?.daily || 1);
          const averageMissed = (missed?.duration?.daily || 0) / (missed?.calls?.daily || 1);

          return {
            name: formatters.date(date),
            values: {
              abandoned: averageAbandoned,
              answered: averageAnswered,
              missed: averageMissed,
            },
          };
        });
      }
    }

    const totals = Object.values(data?.aggregatedMultiLocations ?? {}).reduce<ProcessedTotals>(
      (acc, { calls, duration }) => {
        acc.calls.abandoned = (acc.calls?.abandoned || 0) + (calls?.abandoned || 0);
        acc.calls.answered = (acc.calls?.answered || 0) + (calls?.answered || 0);
        acc.calls.missed = (acc.calls?.missed || 0) + (calls?.missed || 0);
        acc.duration.abandoned = (acc.duration?.abandoned || 0) + (duration?.abandoned || 0);
        acc.duration.answered = (acc.duration?.answered || 0) + (duration?.answered || 0);
        acc.duration.missed = (acc.duration?.missed || 0) + (duration?.missed || 0);
        return acc;
      },
      {
        calls: {
          abandoned: 0,
          answered: 0,
          missed: 0,
        },
        duration: {
          abandoned: 0,
          answered: 0,
          missed: 0,
        },
      }
    );

    return {
      average: {
        abandoned: (totals.duration.abandoned || 0) / (totals.calls.abandoned || 1),
        answered: (totals.duration.answered || 0) / (totals.calls.answered || 1),
        missed: (totals.duration.missed || 0) / (totals.calls.missed || 1),
      },
      chartData: {
        groups,
      },
    };
  }, [accessibleLocationsNames, data, isHourlyData, isMultiLocation]);

  return (
    <Chart colors={colors} isLoading={isLoading} labels={labels}>
      <Chart.Header
        {...exportPdfProps}
        infoTip={
          <InfoTipPopover>
            {t(
              'Incoming: Refers to the average time on Answered, Missed and Abandoned calls. It only includes Incoming calls. Value is in hh:mm:ss'
            )}
          </InfoTipPopover>
        }
        leftElement={isDemoAccount && <DemoChip />}
        subtitle={!isSmallMode && filterHintText}
        title={t('Average Call Duration - Incoming')}
      />
      <Chart.BarChart
        appearance={defaultChartAppearance}
        data={processeData.chartData}
        formatValue={(value) => AnalyticsUtils.toHHmmss(value)}
      />
      <Chart.Legends formatValue={(value) => AnalyticsUtils.toHHmmss(value)} values={processeData.average} />
    </Chart>
  );
};
